import { Injectable, Optional } from '@angular/core';
import { SeFormulaParser } from '@se/se.ui.advanced';
import { DataBinding, Logger } from '@se/se.ui.html5';
import { find, isNull, isUndefined, toString } from 'lodash-es';
import { isMoment } from 'moment';
import { ICheckedEntity, MatchingResult } from '../models/abstractions';
import { ConstraintItemCheckingModeEnum, LogicalOperatorEnum } from '../models/enum';
import { IConstraint, IConstraintBase, IConstraintItem } from '../models/model.constraint';
import { SeConstraintConfiguratorService } from '../se-constraint-configurator.service/se-constraint-configurator.service';
import { Utils } from '../utils/utils';
import {
    SeConstraintItemMatchingResult,
    SeConstraintMatchingResult,
    SeEntityConstraintItemResult,
    SeEntityMatchingResult,
} from '../view-models/view-model.constraint';
import { CheckedDataServiceList } from '../view-models/view-model.dataservice';

@Injectable({
    providedIn: 'root',
})
export class SeConstraintCheckerService {
    //#region constructor and read-only properties
    readonly customFunctions = new Map<string, Function>();

    constructor(
        private configDataService: SeConstraintConfiguratorService,
        @Optional() private logger: Logger,
    ) {}

    get checkedDataServices(): CheckedDataServiceList | null {
        return this.configDataService.checkedDataServices;
    }

    //#endregion

    checkConstraint(
        constraint: IConstraint | null | undefined,
        checkOnlyPartialResults: boolean = false,
    ): SeConstraintMatchingResult[] {
        try {
            if (!constraint) throw new Error("Can't check an undefined constraint.");

            let toIterate = false;
            let iteratorList: ICheckedEntity[] = [];

            if (!!constraint.dataSourceIterator) {
                const iteratorBinding = new DataBinding(constraint.dataSourceIterator);
                iteratorList = this.checkedDataServices?.getEntities(iteratorBinding) ?? [];
                toIterate = true;
            }

            let currentIteration = 0;
            let finalRes: SeConstraintMatchingResult[] = [];

            while ((!toIterate && currentIteration == 0) || (toIterate && currentIteration < iteratorList.length)) {
                if (toIterate) this.checkedDataServices?.setCurrentEntity(iteratorList[currentIteration]);

                let resIteration = this.checkConstraintSingleIteration(constraint, checkOnlyPartialResults);

                finalRes.push(resIteration);

                currentIteration++;
            }

            return finalRes;
        } finally {
            this.checkedDataServices?.resetCurrentEntity();
        }
    }

    addCustomFunctions(customFunctions: [string, Function][]) {
        for (const [name, fn] of customFunctions) {
            if (!this.customFunctions.has(name)) {
                this.customFunctions.set(name, fn);
            }
        }
    }

    private checkConstraintSingleIteration(
        constraint: IConstraint,
        checkOnlyPartialResults: boolean,
    ): SeConstraintMatchingResult {
        if (!constraint) throw new Error("Can't check an undefined constraint.");

        let fakeChecking = false;
        let formulaParser: SeFormulaParser = new SeFormulaParser();

        /*chiamare formulaParser.setFunction sulla lista di funzioni custom esterne*/
        for (const [fnName, fn] of this.customFunctions) {
            formulaParser.setFunction(fnName, fn);
        }

        if (!!constraint.condition) {
            if (!this.checkConstraintBase(constraint.condition, formulaParser).matchingResult === true)
                //se la condizione non ha successo, non devo fare niente e quindi skippo la verifica
                fakeChecking = true;
        }

        //se arrivo qui con fakeChecking = false o non ho condizione o la verifica della condizione ha avuto successo
        //se fakeChecking = true invoco lo stesso il metodo skippando le verifiche
        //(il metodo mi serve comunque per preparare le struttura da ritornare)
        let finalRes = this.checkConstraintBase(constraint, formulaParser, fakeChecking, checkOnlyPartialResults);

        //se fakeChecking è true, significa che la condition è fallita e quindi non ho verificato le regole.
        //Quindi imposto il flag isMatching a null
        if (fakeChecking) finalRes.matchingResult = null;
        else {
            let formulaTarget = this.getFormulaTarget(constraint, formulaParser);

            if (!!formulaTarget) {
                this.setValueIntoFormulaTarget(formulaTarget, finalRes.matchingResult, constraint.dataSourceTarget);
            }
        }

        return finalRes;
    }

    private getFormulaTarget(constraint: IConstraint, formulaParser: SeFormulaParser): string | undefined {
        if (!constraint || !formulaParser) throw new Error("Can't check an undefined constraint.");

        let formulaTarget: string | undefined = constraint.formulaTarget;

        if (!formulaTarget && !!constraint.parameterizedFormulaTarget) {
            const formulaTargetResult = formulaParser.parse(constraint.parameterizedFormulaTarget);

            if (!!formulaTargetResult.error && formulaTargetResult.error !== '') {
                //in caso di errore, loggo l'errore come warning e skippo la regola
                this.logger.debug(
                    `Rule ${constraint.id}. Error in calculating formulaTarget: ${formulaTargetResult.error}`,
                );
                formulaTarget = undefined;
            } else {
                formulaTarget = formulaTargetResult.result;
            }
        }

        return formulaTarget;
    }

    private setValueIntoFormulaTarget(formulaTarget: string, value: unknown, idDataSource?: string) {
        const binding = new DataBinding(formulaTarget);
        const propName = binding.propertyName;

        /*task 97261 - 97262 fix necessario per poter scrivere in un formalaTarget con binding indicizzato (childrenArray)
       scatenato da regola con questo setting:
        "parameterizedFormulaTarget": "CONCATENATE('Report.Examinations.',APPLY_FIND_INDEX_DEEP(C_1,'IdBusinessUnit.IsUs',TRUE),'.IdEquipment')",
        @TODO rivedere il fix appena concluse le emergenze 34.12. cerca di scrivere qualcosa che non sia una patch
       * */
        if (binding.entity.full.findIndex((b) => b.isIndex()) === binding.entity.full.length - 1) {
            this.checkedDataServices?.setValue(binding, value);
            return;
        }

        const entity: ICheckedEntity | undefined = this.checkedDataServices?.getSelectedEntity(binding.entity, true);
        if (isUndefined(entity?.path) || isNull(entity?.path)) return;

        this.logger.debug(`Modifying ${entity?.path}.${propName} by rule.`);

        let valuePath: string;
        if (!!entity?.path) valuePath = entity.path + '.' + propName;
        //root property
        else valuePath = propName;

        const valueBinding = new DataBinding(valuePath);

        if (!idDataSource) this.checkedDataServices?.setValue(valueBinding, value);
        else this.checkedDataServices?.setDatasourceValue(valueBinding, toString(value), idDataSource);
    }

    public checkConstraintItem(
        constraintItem: IConstraintItem | undefined | null,
        fakeChecking: boolean = false,
    ): SeEntityConstraintItemResult[] {
        if (!constraintItem) throw new Error("Can't check an undefined constraint item.");

        switch (constraintItem.checkingMode) {
            case ConstraintItemCheckingModeEnum.OnlySelectedEntity:
                return this.checkConstraintItemOnSelectedEntity(constraintItem, fakeChecking);
            case ConstraintItemCheckingModeEnum.EntityList:
                return this.checkConstraintItemOnEntityList(constraintItem, fakeChecking);
            default:
                return this.checkConstraintItemOnAllEntities(constraintItem, fakeChecking);
        }
    }

    public checkConstraintItemOnSpecificEntity(
        constraintItem: IConstraintItem,
        currentEntity: ICheckedEntity | null,
        fakeChecking: boolean = false,
        onEntityList: boolean = false,
    ): MatchingResult {
        //il flag fakeChecking mi serve perchè in caso di verifica fallita sulla condizione
        //non devo fare alcuna verifica, ma semplicemente popolare il risultato
        //con i constraint item e le entity corrispondenti

        let propertyName = new DataBinding(constraintItem.propertyId).propertyName;

        let matchingResult: MatchingResult = null;
        if (!fakeChecking) {
            let matchValue: unknown = constraintItem.matchValue;
            if (!!constraintItem.matchPropertyId) {
                let matchingBinding = new DataBinding(constraintItem.matchPropertyId);
                /** @FIX ho modificato matchingBinding -> matchingBinding.entity */
                let matchingEntity: ICheckedEntity | undefined = this.checkedDataServices?.getSelectedEntity(
                    matchingBinding.entity,
                );

                matchValue = this.getEntityPropertyValue(matchingEntity ?? null, matchingBinding.propertyName);
            }

            matchingResult = this.checkEntityProperty(
                currentEntity,
                propertyName,
                matchValue,
                onEntityList,
                constraintItem.logicalOperator,
            );

            if (!!constraintItem.listPropertyTarget && !!matchingResult)
                matchingResult = matchingResult[constraintItem.listPropertyTarget];
        }

        return matchingResult;
    }

    //#region privates

    private getEntityPropertyValue(entity: ICheckedEntity | null, propertyName: string): unknown {
        let propertyValue: any;
        if (!entity) {
            propertyValue = null; //entity vuota
        } else {
            if (!!entity.getPropertyPrimitiveValue) propertyValue = entity.getPropertyPrimitiveValue(propertyName);
            else propertyValue = entity[propertyName as keyof ICheckedEntity];
        }

        return propertyValue;
    }

    private checkConstraintItemOnSelectedEntity(
        constraintItem: IConstraintItem,
        fakeChecking: boolean,
    ): SeEntityConstraintItemResult[] {
        let binding = new DataBinding(constraintItem.propertyId);

        let selectedEntity: ICheckedEntity | undefined = this.checkedDataServices?.getSelectedEntity(binding.entity);

        let matchingResult: MatchingResult = this.checkConstraintItemOnSpecificEntity(
            constraintItem,
            selectedEntity ?? null,
            fakeChecking,
        );

        let results: SeEntityConstraintItemResult[] = [];
        results.push(
            new SeEntityConstraintItemResult(
                selectedEntity,
                new SeConstraintItemMatchingResult(constraintItem, matchingResult),
            ),
        );

        return results;
    }

    private checkConstraintItemOnAllEntities(
        constraintItem: IConstraintItem,
        fakeChecking: boolean,
    ): SeEntityConstraintItemResult[] {
        //la condizione deve essere verificata su tutte le entity
        let binding = new DataBinding(constraintItem.propertyId);
        let entities = this.checkedDataServices?.getEntities(binding.entity);

        let results: SeEntityConstraintItemResult[] = [];

        let entity: ICheckedEntity;
        for (entity of entities ?? []) {
            let matchingResult: MatchingResult = this.checkConstraintItemOnSpecificEntity(
                constraintItem,
                entity,
                fakeChecking,
            );

            let currentRes = new SeEntityConstraintItemResult(
                entity,
                new SeConstraintItemMatchingResult(constraintItem, matchingResult),
            );

            results.push(currentRes);
        }

        return results;
    }

    private checkConstraintItemOnEntityList(
        constraintItem: IConstraintItem,
        fakeChecking: boolean,
    ): SeEntityConstraintItemResult[] {
        //la regola viene verificata sulla lista

        let binding = new DataBinding(constraintItem.propertyId);

        //TODO: questa riga qua sotto è probabilmente sbagliata, da rivedere (dall'array di entity bisogna tirare fuori l'array di proprietà)
        let entityList = <ICheckedEntity>(<unknown>this.checkedDataServices?.getEntities(binding.entity));

        let matchingResult: MatchingResult = this.checkConstraintItemOnSpecificEntity(
            constraintItem,
            entityList,
            fakeChecking,
            true,
        );

        let results: SeEntityConstraintItemResult[] = [];
        results.push(
            new SeEntityConstraintItemResult(
                entityList,
                new SeConstraintItemMatchingResult(constraintItem, matchingResult),
            ),
        );

        return results;
    }

    private checkEntityProperty(
        currentEntity: ICheckedEntity | null,
        propertyName: string,
        matchValue: unknown,
        onEntityList: boolean,
        logicalOperator?: LogicalOperatorEnum,
    ): MatchingResult {
        let valueToCheck: unknown = onEntityList
            ? currentEntity
            : this.getEntityPropertyValue(currentEntity, propertyName);
        let itemListPropertyName = onEntityList ? propertyName : undefined;

        if (logicalOperator == null) {
            return valueToCheck;
        }

        return Utils.checkValue(valueToCheck, matchValue, logicalOperator, itemListPropertyName);
    }

    /**
     * Questo metodo prende in ingresso un insieme di constrait items. Per ogni constraint item viene verificata la regola associata
     * e calcolato il risultato globale mettendo in AND/OR i risultati delle verifiche sulle singole entità.
     * Il metodo ritorna due array: nel primo sono inseriti i risultati di ogni singola entity su ogni singolo constraint,
     * nel secondo i risultati globali di ogni constraint item.
     * @param constraintItems L'array di constraint items da verificare
     * @param fakeChecking False di default. Se true, skippa la verifica del constraint.
     */
    private checkArrayOfConstraintItems(
        constraintItems: IConstraintItem[],
        fakeChecking: boolean = false,
    ): [SeEntityConstraintItemResult[], SeConstraintItemMatchingResult[]] {
        let allConstraintItemsEntityResults: SeEntityConstraintItemResult[] = [];
        let allConstraintItemsGlobalResults: SeConstraintItemMatchingResult[] = [];

        //per ogni constraint item...
        for (let constraintItem of constraintItems) {
            //verifico l'item: viene restituito un risultato per ogni entity in gioco
            let constraintItemResults: SeEntityConstraintItemResult[] = this.checkConstraintItem(
                constraintItem,
                fakeChecking,
            );

            //calcola il risultato globale della verifica del constraint item
            //(il risultato su ogni singola entity viene messo in AND / OR a seconda della proprietà checkingMode)
            let constraintItemGlobalMatching = this.getConstraintItemGlobalMatching(
                constraintItem,
                constraintItemResults,
            );

            //concateno i risultati di tutti gli item
            allConstraintItemsEntityResults = allConstraintItemsEntityResults.concat(constraintItemResults);
            allConstraintItemsGlobalResults.push(constraintItemGlobalMatching);
        }

        return [allConstraintItemsEntityResults, allConstraintItemsGlobalResults];
    }

    /**
     * Organizza per entity un elenco di verifiche di constraint item.
     * Gli oggetti ritornati sono di tipo SeEntityMatchingResult ed hanno a proprietà isMatching impostata a null
     * (il metodo riorganizza solamente la struttura e non calcola risultati).
     * @param constraintItems Array contenente l'elenco di tutto i constraint item
     * @param allConstraintItemsEntityResults Array contenente i risultati di ogni singola entity su ogni singolo constraint item
     */
    private organizeConstraintItemResultsPerEntity(
        constraintItems: IConstraintItem[],
        allConstraintItemsEntityResults: SeEntityConstraintItemResult[],
    ): SeEntityMatchingResult[] {
        let entityMatchingResults: SeEntityMatchingResult[] = [];

        //Construisco (senza per ora calcolare il risultato) l'array dei risultati dei constraint per ogni entity

        //per ogni entity
        for (let entityItemResult of allConstraintItemsEntityResults) {
            //guardo se la ho già aggiunta all'array che poi devo restituire
            let entityIndex = entityMatchingResults.findIndex(
                (res) => res.checkedEntity == entityItemResult.checkedEntity,
            );

            let entityResults: SeEntityMatchingResult;
            if (entityIndex < 0) {
                //se non l'ho già aggiunta, la creo,
                //la memorizzo nella variabile entityResults e la aggiungo all'array
                entityResults = this.createEmptyEntityMatchingResult(
                    constraintItems,
                    entityItemResult.checkedEntity ?? null,
                );
                entityMatchingResults.push(entityResults);
            } else {
                //se l'ho già aggiunta, memorizzo quella già presente nell'array nella variabile entityResults
                entityResults = entityMatchingResults[entityIndex];
            }

            //aggiorno constraintItemResults dentro la variabile entityResults con i risultati del constraint item corrente
            const currentConstraintItemResult: SeConstraintItemMatchingResult | undefined = find(
                entityResults.constraintItemResults,
                (ciRes) =>
                    ciRes.constraintItem?.id.toLowerCase() ===
                    entityItemResult.constraintItemResult?.constraintItem?.id.toLowerCase(),
            );
            if (currentConstraintItemResult)
                currentConstraintItemResult.matchingResult = entityItemResult.constraintItemResult.matchingResult;
        }

        return entityMatchingResults;
    }

    private createEmptyEntityMatchingResult(
        constraintItems: IConstraintItem[],
        checkedEntity: ICheckedEntity | null,
    ): SeEntityMatchingResult {
        const constraintItemsResults: SeConstraintItemMatchingResult[] = [];
        for (let ci of constraintItems) {
            constraintItemsResults.push(new SeConstraintItemMatchingResult(ci, null));
        }

        return new SeEntityMatchingResult(checkedEntity, null, constraintItemsResults);
    }

    /**
     * Verifica un ConstraintBase.
     * @param constraintBase
     * @param formulaParser
     * @param fakeChecking False di default. Se true, skippa la verifica del constraint.
     * @param checkOnlyPartialResults Se true, vengono calcolati solo i risultati parziali del constraint
     * e viene restituito null come risultato globale.
     */
    private checkConstraintBase(
        constraintBase: IConstraintBase,
        formulaParser: SeFormulaParser,
        fakeChecking: boolean = false,
        checkOnlyPartialResults: boolean = false,
    ): SeConstraintMatchingResult {
        if (!constraintBase.constraintItems || constraintBase.constraintItems.length === 0) {
            const formula = !constraintBase.formula ? 'C_0' : constraintBase.formula;
            if (formula.indexOf('C_') >= 0) {
                //se entro qui dentro la formula prevede dei constraint items
                throw new Error('Formula ' + formula + ' expects at least one constraint item.');
            }
        }

        let [allConstraintItemsEntityResults, allConstraintItemsGlobalResults] = this.checkArrayOfConstraintItems(
            constraintBase.constraintItems,
            fakeChecking,
        );

        let entityMatchingResults: SeEntityMatchingResult[] = this.organizeConstraintItemResultsPerEntity(
            constraintBase.constraintItems,
            allConstraintItemsEntityResults,
        );

        let constraintMatchingResult: MatchingResult;

        //per ogni entity in gioco...
        if (entityMatchingResults.length > 0) {
            for (let i = 0; i < entityMatchingResults.length; i++) {
                //calcolo il risultato totale della verifica del constraint su quella entity
                let entityResults: SeEntityMatchingResult = entityMatchingResults[i];
                entityMatchingResults[i] = this.recalculateConstraintResultForASpecificEntity(
                    constraintBase,
                    formulaParser,
                    entityResults,
                    allConstraintItemsGlobalResults,
                    checkOnlyPartialResults,
                );

                constraintMatchingResult = this.addEntityResultToGlobalResult(
                    entityMatchingResults[i],
                    constraintMatchingResult,
                );
            }
        } else {
            let entityResults = new SeEntityMatchingResult(null, null, []);
            entityResults = this.recalculateConstraintResultForASpecificEntity(
                constraintBase,
                formulaParser,
                entityResults,
                allConstraintItemsGlobalResults,
                checkOnlyPartialResults,
            );
            constraintMatchingResult = entityResults.matchingResult;
        }

        return new SeConstraintMatchingResult(constraintBase, constraintMatchingResult, entityMatchingResults);
    }

    /**
     * Aggiorna il calcolo globale del risultato di un constraint, includendo anche  il risultato su una determinata entity.
     */
    private addEntityResultToGlobalResult(
        entityResults: SeEntityMatchingResult,
        entitiesGlobalResult?: MatchingResult,
    ): MatchingResult {
        if (isUndefined(entitiesGlobalResult)) {
            //se non ho un risultato globale, prendo quello della entity come buono
            entitiesGlobalResult = entityResults.matchingResult;
        } else {
            if (entityResults.matchingResult == null) {
                //skippo il constraint
                entitiesGlobalResult = null;
            } else if (entityResults.matchingResult === false) {
                //se per una entità il constraint è fallito, allora considero fallito il constraint
                entitiesGlobalResult = false;
            }
            //else: il risultato sulla entity è positivo quindi non influisce sul risultato finale. Di conseguenza prendo come risultato quello che mi viene passato in input
        }

        return entitiesGlobalResult;
    }

    //L'algortimo di calcolo di un constraint è più difficile spiegarlo che farlo,
    //per cui proverò con un esempio:
    //Supponiamo si avere un constraint C, composto da due constraint items C1 e C2 e fatto in questa maniera qua:
    //C = C1 OR C2, dove
    //C1 = lesioneCoronarica.StenosiPost = 0(per ogni lesione)
    //C2 = procedura.IsApproved = 0
    //Quindi "o ho tutte le stenosi allo 0% oppure ho la procedura non definitiva"
    //Supponiamo che ho 3 lesioni coronariche (L1, L2, L3), una con stenosi allo 0% e le altre due no,
    //quindi il primo constraint item ha come risultato FALSE (i risultati sulle 3 entity sono messi in AND).
    //Poi supponiamo che la procedura non è definitiva, quindi il risultato del secondo constraint item è TRUE.
    //E' evidente quindi che il constraint ha successo perchè il secondo vincolo è TRUE e i due vincoli sono in OR.
    //Nello specifico, per ogni entity:
    //I risultati per il constraint item C1 sono: L1: true, L2: false, L3: false
    //I risultati per il constraint item C2 è true per la entity "Procedura"
    //Nell'algoritmo per calcolare il risultato finale, prima calcolo il risultato di ogni singola entity:
    //L1: True OR RISULTATO(C2) = True OR True = True
    //L2: False OR RISULTATO(C2) = False OR True = True
    //L3: False OR RISULTATO(C2) = False OR True = True
    //PROC: RISULTATO(C1) OR TRUE = False OR True = True
    //Ho quindi True per tutte e 4 le entity e di conseguenza il risultato finale è TRUE (i risultati sulle entity sono messi in AND tra di loro).
    //
    //Diversamente Se avessi avuto un constraint fatto così: C = C1 AND C2, per ogni singola entity avrei avuto:
    //L1: True AND True = True
    //L2: False AND True = False
    //L3: False AND True = False
    //PROC: False AND True = False
    //Res(C) = True AND False AND False And False = False, quindi il constraint non avrebbe avuto successo
    //
    //In caso di formula più complessa l'algoritmo sarebbe stato analogo.
    //Per esempio: C = NOT(C2) OR (C1 AND C2)
    //Seguendo l'algoritmo sopra diventerebbe:
    //L1: NOT(Res(C2)) OR (True AND Res(C2))  = NOT(True) OR (True AND True) = False Or True = True
    //L2: NOT(Res(C2)) OR (False AND Res(C2)) = NOT(True) OR (False AND True) = False Or False = False
    //L3: NOT(Res(C2)) OR (False AND Res(C2)) = NOT(True) OR (False AND True) = False Or False = False
    //PROC: NOT(True) OR (Res(C1) AND True) = NOT(True) OR (False AND True) = False Or False = False
    //Res(C) = True AND False AND False AND False = False, quindi il constraint non avrebbe avuto successo
    //
    //Nel caso di valori non booleani il calcolo è analogo.
    //Supponiamo di avere 3 schede di cateterismo CATH1, CATH2, CATH3 e che, per ogni scheda,
    //si debba calcolare la saturazione per l'emoglobina, dato invece unico in procedura.
    //La formula sarebbe C= C0 * C1
    //dove C0: CATH.Sat per le entity CATH1, CATH2 e CATH3
    //e C1: PROC.Hemo
    //Supponiamo poi che CATH1.Sat = 10, CATH2.Sat = 20, CATH3.Sat = 30
    //e che PROC.Hemo = 15
    //Seguendo l'algoritmo specificato sopra, i passi per il calcolo sarebbero i seguenti:
    //CATH1: RES(C1 per CATH1)*RES(C2) = 10*15 = 150
    //CATH2: RES(C1 per CATH2)*RES(C2) = 20*15 = 300
    //CATH3: RES(C1 per CATH3)*RES(C2) = 30*15 = 450
    //PROC: RES(C1)*RES(C2) = NULL * 15 = NULL
    //In questo caso, il risultato globale su C1 è null perchè non è possibile calcolare un risultato su più entità non booleane
    //Quindi RES(C) = NULL, ma i risultati parziali conterrano i singoli valori calcolati
    private recalculateConstraintResultForASpecificEntity(
        constraintBase: IConstraintBase,
        formulaParser: SeFormulaParser,
        entityResults: SeEntityMatchingResult,
        constraintItemsGlobalResults: SeConstraintItemMatchingResult[],
        checkOnlyPartialResults: boolean,
    ): SeEntityMatchingResult {
        //il sort aggiorna anche l'array di constraint items. Lo assegno ad una variabile per leggibilità
        //e per evitare di scrivere nel for sotto constraintBase.constraintItems...
        const orderedConstraintItems = constraintBase.constraintItems.sort((a, b) => a.order - b.order);

        //per ogni constraint item...
        let i = 0;
        for (let constraintItem of orderedConstraintItems) {
            let constraintItemId = constraintItem.id.toLowerCase();
            //recupero il risultato della verifica dell'item
            let constraintItemEntityResult = entityResults.constraintItemResults?.find(
                (item) => item.constraintItem?.id.toLowerCase() === constraintItemId,
            );
            let constraintItemResult: MatchingResult;

            if (
                Utils.isEmpty(constraintItemEntityResult) ||
                Utils.isEmpty(constraintItemEntityResult.matchingResult) ||
                (!checkOnlyPartialResults &&
                    constraintItem.checkingMode !== ConstraintItemCheckingModeEnum.OnlySelectedEntity)
            ) {
                //non c'è un risultato per questa entity o il risultato del constraint item impatta su tutte le entities,
                //prendo il risultato tra i risultati globali dei constraint
                let globalResult = constraintItemsGlobalResults.find(
                    (item) => item.constraintItem?.id.toLowerCase() === constraintItemId,
                );
                if (!globalResult) {
                    entityResults.matchingResult = null;
                    //se non c'è neanche tra i risultati globali skippo il constraint
                    return entityResults;
                }

                constraintItemResult = globalResult.matchingResult;
            } else constraintItemResult = constraintItemEntityResult.matchingResult;

            if (isMoment(constraintItemResult)) constraintItemResult = constraintItemResult.toDate();

            formulaParser.setVariable('C_' + i, constraintItemResult);
            i++;
        }
        let formula = constraintBase.formula;
        if (!formula || formula == '') {
            if (orderedConstraintItems.length === 1) {
                //se la formula è nulla, nel caso di un solo item restituisco il risultato della verifica dell'item
                formula = 'C_0';
            } else {
                //loggo un errore e skippo la regola perchè non so cosa tornare
                this.logger &&
                    this.logger.error(
                        `The formula is not valid for the rule ${constraintBase.id} and it'll be skipped.`,
                    );
                entityResults.matchingResult = null;
                return entityResults;
            }
        }

        let formulaResult = formulaParser.parse(formula);

        if (!!formulaResult.error && formulaResult.error !== '') {
            //in caso di errore, loggo l'errore come warning e skippo la regola
            if (formulaResult.error === '#ERROR!')
                this.logger.error(
                    `Rule ${constraintBase.id}.${constraintBase.name} Error in calculating formula: ${formulaResult.error} - Please, verify the correctness of the formula.`,
                );
            else
                this.logger.debug(
                    `Rule ${constraintBase.id}.${constraintBase.name} Error in calculating formula: ${formulaResult.error}`,
                );
            entityResults.matchingResult = null;
        } else {
            entityResults.matchingResult = formulaResult.result;
        }

        return entityResults;
    }

    /**
     * Dato un array di risultati di una verifica di un constraint item per entity,
     * viene restituito un risultato globale per quel constraint item,
     * mettendo in AND o in OR i risultati su ogni singola entity a seconda del ConstraintItemCheckingMode
     */
    public getConstraintItemGlobalMatching(
        constraintItem: IConstraintItem,
        entityConstraintItemResults: SeEntityConstraintItemResult[],
    ): SeConstraintItemMatchingResult {
        let specificConstraintItemResults = entityConstraintItemResults.filter(
            (item) => item.constraintItemResult?.constraintItem?.id.toLowerCase() === constraintItem.id.toLowerCase(),
        );

        let globalMatching: MatchingResult;

        if (specificConstraintItemResults.some((cir) => cir.constraintItemResult.matchingResult == null)) {
            globalMatching = null;
        } else if (
            constraintItem.checkingMode == ConstraintItemCheckingModeEnum.OnlySelectedEntity ||
            constraintItem.checkingMode == ConstraintItemCheckingModeEnum.EntityList
        ) {
            globalMatching = entityConstraintItemResults[0].constraintItemResult.matchingResult;
        } else if (constraintItem.checkingMode == ConstraintItemCheckingModeEnum.AllEntities) {
            globalMatching = specificConstraintItemResults.reduce(
                (partial: boolean, currentResult: SeEntityConstraintItemResult) =>
                    partial && currentResult.constraintItemResult.matchingResult,
                true,
            );
        } else if (constraintItem.checkingMode == ConstraintItemCheckingModeEnum.AlmostOneEntity) {
            globalMatching = specificConstraintItemResults.reduce(
                (partial: boolean, currentResult: SeEntityConstraintItemResult) =>
                    partial || currentResult.constraintItemResult.matchingResult,
                false,
            );
        } else globalMatching = null;

        return new SeConstraintItemMatchingResult(constraintItem, globalMatching);
    }

    //#endregion
}
